import { agentExecutorLabel } from '@fos/shared/labels';
import { Bot, ChevronDown } from 'lucide-react';
import { cn } from '../../../../lib/cn';
import { formatRelativePast } from '../../../../lib/format';
import { trpc } from '../../../../lib/trpc';
import { useAccess } from '../../../../lib/useAccess';
import { ErrorState, ListRow, ListSkeleton, Mono, Section, Status } from '../../../../ui';
import { formatRunDuration, runPath, runStatusLabel, runStatusTone } from '../../../agents/format';

/**
 * Przebiegi AI zadania (kompozycja Fali 5): stopka karty „AI · 2 przebiegi” (jak „Czas” i „Historia”, Figma 04)
 * rozwija listę wszystkich przebiegów zadania w zakresie osoby (najnowszy pierwszy) — status, wykonawca, kiedy, czas
 * pracy, link do przebiegu. Ostatni przebieg ma też blok wyniku nad stopką (`AiResultBlock`). Bez modułu Agenci
 * albo bez przebiegów — nic.
 */
const TASK_RUNS_LIMIT = 20;

function useTaskRuns(taskId: string, enabled: boolean) {
  return trpc.agents.list.useQuery({ scope: 'team', taskId, limit: TASK_RUNS_LIMIT }, { enabled });
}

/** „1 przebieg”, „2 przebiegi”, „5 przebiegów”. */
export function runsCountLabel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return '1 przebieg';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} przebiegi`;
  return `${n} przebiegów`;
}

export function TaskRunsToggle({ taskId, open, onToggle }: { taskId: string; open: boolean; onToggle: () => void }) {
  const access = useAccess();
  const enabled = access.can('agents');
  const runs = useTaskRuns(taskId, enabled);
  const count = runs.data?.items.length ?? 0;
  if (!enabled || count === 0) return null;
  const more = runs.data?.nextCursor ? '+' : '';
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls="przebiegi-zadania"
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 rounded-sm hover:text-fg focus-visible:outline-2 focus-visible:outline-accent max-md:min-h-11"
    >
      <Bot size={14} strokeWidth={1.75} aria-hidden />
      AI · {runsCountLabel(count)}
      {more}
      <ChevronDown
        size={14}
        strokeWidth={1.75}
        aria-hidden
        className={cn('transition-transform', open && 'rotate-180')}
      />
    </button>
  );
}

export function TaskRuns({ taskId }: { taskId: string }) {
  const access = useAccess();
  const runs = useTaskRuns(taskId, access.can('agents'));
  return (
    <Section id="przebiegi-zadania" aria-label="Przebiegi AI zadania" className="mt-4">
      {runs.isPending ? (
        <ListSkeleton rows={2} />
      ) : runs.isError ? (
        <ErrorState compact title="Nie udało się wczytać przebiegów" onRetry={() => void runs.refetch()} />
      ) : (
        <ul>
          {runs.data.items.map((run) => (
            <li key={run.id}>
              <ListRow
                to={runPath(run.id)}
                height="l"
                title={`Przebieg #${run.number}`}
                description={[agentExecutorLabel(run), formatRelativePast(run.createdAt), run.createdBy.name]
                  .filter(Boolean)
                  .join(' · ')}
                trailing={
                  <>
                    <Mono className="hidden text-caption text-fg-muted sm:inline">
                      {formatRunDuration(run.durationSec)}
                    </Mono>
                    <Status tone={runStatusTone(run)} className="min-w-0 truncate">
                      {runStatusLabel(run)}
                    </Status>
                  </>
                }
                chevron
              />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
